import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useBranding, themeStyle } from "../context/BrandingContext";
import { api } from "../lib/api";
import { Avatar } from "./fields";
import { CalendarCheck, LayoutGrid, ClipboardList, Users, FileText, LogOut, NotebookPen, BarChart3, MessageSquareText, Search, Bell, ChevronDown, Menu, X, LifeBuoy, Target } from "lucide-react";

const NAV = {
  aide: [
    { to: "/aide", label: "Today", icon: CalendarCheck },
    { to: "/coaching", label: "Coaching", icon: MessageSquareText },
    { to: "/weekly-review", label: "Weekly Review", icon: NotebookPen },
    { to: "/forms", label: "Forms", icon: FileText },
  ],
  coordinator: [
    { to: "/overview", label: "Overview", icon: LayoutGrid },
    { to: "/goals", label: "Goal Check", icon: Target, needsGoalCheck: true },
    { to: "/reports", label: "Reports", icon: BarChart3 },
    { to: "/forms", label: "Forms", icon: FileText },
  ],
  school_admin: [
    { to: "/overview", label: "Overview", icon: LayoutGrid },
    { to: "/goals", label: "Goal Check", icon: Target, needsGoalCheck: true },
    { to: "/reports", label: "Reports", icon: BarChart3 },
    { to: "/forms", label: "Forms", icon: FileText },
  ],
  supervisor: [
    { to: "/supervisor", label: "Review Queue", icon: ClipboardList },
    { to: "/overview", label: "Overview", icon: LayoutGrid },
    { to: "/goals", label: "Goal Check", icon: Target },
    { to: "/forms", label: "Forms", icon: FileText },
  ],
  sped_teacher: [
    { to: "/sped", label: "My Students", icon: Users },
    { to: "/goals", label: "Goal Check", icon: Target },
    { to: "/forms", label: "Forms", icon: FileText },
  ],
  tag_admin: [
    { to: "/admin", label: "Admin", icon: Users },
    { to: "/overview", label: "Overview", icon: LayoutGrid },
    { to: "/supervisor", label: "Review Queue", icon: ClipboardList },
    { to: "/goals", label: "Goal Check", icon: Target },
    { to: "/forms", label: "Forms", icon: FileText },
  ],
};

const ROLE_LABEL = { aide: "Aide", coordinator: "School Coordinator", school_admin: "School Administrator", supervisor: "TAG Supervisor", sped_teacher: "TAG Special Education Teacher", tag_admin: "TAG Admin" };

const Brand = () => {
  const { branding } = useBranding();
  const themed = branding?.theme;
  return (
    <div className="flex items-center gap-2.5">
      {themed && branding.logo ? (
        <img src={branding.logo} alt={branding.school_name} data-testid="school-brand-logo" className="h-9 w-auto max-w-[120px] rounded-lg object-contain" />
      ) : (
        <span className="w-9 h-9 rounded-xl flex items-center justify-center font-heading font-bold text-white text-sm shadow-sm"
          style={{ backgroundColor: themed ? "var(--brand)" : "#087F5B" }}>SP</span>
      )}
      <div>
        <p className="font-heading text-base font-bold text-[#132238] tracking-tight leading-none">SupportPro Hub</p>
        <p className="text-[10px] mt-0.5" style={{ color: themed ? "var(--brand-accent)" : "#667085", fontWeight: themed ? 600 : 400 }}
          data-testid="brand-subtitle">{themed ? branding.school_name : "by The Allicock Group"}</p>
      </div>
    </div>
  );
};

const NavItems = ({ items, unread, onNavigate }) => (
  <nav className="flex-1 px-3 space-y-1">
    {items.map(({ to, label, icon: Icon }) => (
      <NavLink
        key={to}
        to={to}
        onClick={onNavigate}
        data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-2.5 rounded-[10px] text-sm transition-colors duration-200 ${
            isActive ? "bg-[#EAF8F3] text-[#066548] font-semibold" : "text-[#667085] font-medium hover:bg-slate-50 hover:text-[#132238]"
          }`
        }
      >
        {({ isActive }) => (
          <>
            <Icon size={17} className={isActive ? "text-[#087F5B]" : ""} /> {label}
            {label === "Coaching" && unread > 0 && (
              <span data-testid="coaching-unread-badge" className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-[#087F5B] text-white text-[10px] font-bold flex items-center justify-center">{unread}</span>
            )}
          </>
        )}
      </NavLink>
    ))}
  </nav>
);

const GlobalSearch = ({ role }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const students = useRef(null);
  const navigate = useNavigate();

  const search = async (val) => {
    setQ(val);
    if (!val.trim()) { setResults([]); setOpen(false); return; }
    if (!students.current) {
      try { students.current = (await api.get("/students")).data; } catch { students.current = []; }
    }
    const v = val.toLowerCase();
    setResults(students.current.filter((s) => `${s.first_name} ${s.last_name}`.toLowerCase().includes(v)).slice(0, 6));
    setOpen(true);
  };

  const go = (s) => {
    setOpen(false); setQ("");
    navigate(role === "aide" ? `/students/${s.id}` : `/students/${s.id}/dashboard`);
  };

  return (
    <div className="relative hidden md:block w-72 lg:w-96">
      <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#667085]" />
      <input
        data-testid="global-search-input"
        value={q}
        onChange={(e) => search(e.target.value)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Search students..."
        className="w-full rounded-[10px] border border-[#E5E9EE] bg-[#F7F9FA] pl-9 pr-3 py-2 text-sm text-[#132238] placeholder:text-[#98A2B3] focus:outline-none focus:ring-2 focus:ring-[#087F5B]/40 focus:bg-white transition-colors duration-150"
      />
      {open && results.length > 0 && (
        <div className="absolute top-full mt-1.5 w-full bg-white border border-[#E5E9EE] rounded-xl shadow-lg overflow-hidden z-50">
          {results.map((s) => (
            <button key={s.id} data-testid={`search-result-${s.id}`} onMouseDown={() => go(s)}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-[#F7F9FA] transition-colors duration-150">
              <Avatar name={`${s.first_name} ${s.last_name}`} size="sm" tone="mint" />
              <div>
                <p className="text-sm font-medium text-[#132238]">{s.first_name} {s.last_name}</p>
                <p className="text-[11px] text-[#667085]">Grade {s.grade}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCoaching, setUnreadCoaching] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const items = (NAV[user?.role] || []).filter((i) => !i.needsGoalCheck || branding?.goal_check_enabled);

  useEffect(() => {
    if (user?.role !== "aide") return;
    const fetchUnread = () => api.get("/coaching-comments/unread-count").then((r) => setUnreadCoaching(r.data.count)).catch(() => {});
    fetchUnread();
    const t = setInterval(fetchUnread, 60000);
    const clear = () => setUnreadCoaching(0);
    window.addEventListener("coaching-read", clear);
    return () => { clearInterval(t); window.removeEventListener("coaching-read", clear); };
  }, [user?.role, location.pathname]);

  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  const today = new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
  const doLogout = () => { logout(); navigate("/login"); };
  const inPreview = !!user?.preview;

  const exitPreview = async () => {
    try { await api.post("/admin/preview/end"); } catch { /* audit best-effort */ }
    const backup = localStorage.getItem("sf_admin_token");
    if (backup) localStorage.setItem("sf_token", backup);
    else localStorage.removeItem("sf_token");
    localStorage.removeItem("sf_admin_token");
    window.location.href = backup ? "/admin" : "/login";
  };

  const ProfileCard = () => (
    <div className="p-3 border-t border-[#E5E9EE] space-y-2.5">
      <div className="mx-1 rounded-xl bg-[#F7F9FA] border border-[#E5E9EE] p-3">
        <p className="text-xs font-semibold text-[#132238] flex items-center gap-1.5"><LifeBuoy size={13} className="text-[#087F5B]" /> Need help?</p>
        <a href="/support" className="text-[11px] text-[#087F5B] font-medium hover:underline">Visit Help Center</a>
      </div>
      <div className="flex items-center gap-2.5 px-2 py-1.5">
        <Avatar name={user?.name} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-[#132238] truncate">{user?.name}</p>
          <p className="text-[11px] text-[#667085] truncate">{ROLE_LABEL[user?.role]}</p>
        </div>
        <button data-testid="logout-btn" onClick={doLogout} title="Secure logout"
          className="p-2 rounded-lg text-[#667085] hover:text-[#132238] hover:bg-slate-100 transition-colors duration-200">
          <LogOut size={15} />
        </button>
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen bg-[#F7F9FA] flex ${branding?.theme ? "sf-themed" : ""}`} style={themeStyle(branding?.theme)}>
      {inPreview && (
        <div data-testid="preview-banner" className="no-print fixed top-0 inset-x-0 z-[100] h-10 bg-[#132238] text-white text-[13px] font-semibold px-3 flex items-center justify-center gap-2 flex-wrap">
          <span data-testid="preview-banner-label" className="truncate">Previewing as {user.preview_label} at {user.preview_school_name}</span>
          <button data-testid="exit-preview-btn" onClick={exitPreview}
            className="shrink-0 bg-white/15 hover:bg-white/25 px-3 py-1 rounded-full text-xs font-bold transition-colors duration-150">Exit preview</button>
        </div>
      )}
      <aside className={`no-print hidden md:flex flex-col w-[260px] shrink-0 bg-white border-r border-[#E5E9EE] min-h-screen sticky top-0 h-screen ${inPreview ? "pt-10" : ""}`}>
        <div className="px-5 py-6"><Brand /></div>
        <p className="px-6 pb-2 text-[11px] font-semibold text-[#98A2B3]">Workspace</p>
        <NavItems items={items} unread={unreadCoaching} />
        <ProfileCard />
      </aside>

      {drawerOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-[#0F1F38]/50 sf-overlay" onClick={() => setDrawerOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-[280px] bg-white flex flex-col sf-sheet">
            <div className="px-5 py-5 flex items-center justify-between">
              <Brand />
              <button data-testid="drawer-close-btn" onClick={() => setDrawerOpen(false)} className="p-2 text-[#667085]"><X size={18} /></button>
            </div>
            <NavItems items={items} unread={unreadCoaching} onNavigate={() => setDrawerOpen(false)} />
            <ProfileCard />
          </div>
        </div>
      )}

      <div className={`flex-1 flex flex-col min-w-0 ${inPreview ? "pt-10" : ""}`}>
        <header className={`no-print sticky ${inPreview ? "top-10" : "top-0"} z-30 bg-white border-b border-[#E5E9EE] h-[64px] px-4 md:px-8 flex items-center justify-between gap-3`}>
          <div className="flex items-center gap-3 min-w-0">
            <button data-testid="mobile-menu-btn" onClick={() => setDrawerOpen(true)} className="md:hidden p-2 -ml-2 text-[#667085]"><Menu size={20} /></button>
            <span className="md:hidden"><Brand /></span>
            <p className="hidden md:block text-sm text-[#667085]">{today}</p>
          </div>
          <div className="flex items-center gap-2.5">
            <GlobalSearch role={user?.role} />
            <button data-testid="notification-bell" onClick={() => navigate(user?.role === "aide" ? "/coaching" : items[0]?.to || "/")}
              className="relative p-2 rounded-lg text-[#667085] hover:text-[#132238] hover:bg-slate-50 transition-colors duration-150" title="Notifications">
              <Bell size={17} />
              {unreadCoaching > 0 && <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500" />}
            </button>
            <div className="relative">
              <button data-testid="profile-menu-btn" onClick={() => setMenuOpen((v) => !v)} onBlur={() => setTimeout(() => setMenuOpen(false), 150)}
                className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-slate-50 transition-colors duration-150">
                <Avatar name={user?.name} size="sm" />
                <span className="hidden lg:block text-xs font-semibold text-[#132238] max-w-[120px] truncate">{user?.name}</span>
                <ChevronDown size={14} className="text-[#667085]" />
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-full mt-1.5 w-52 bg-white border border-[#E5E9EE] rounded-xl shadow-lg overflow-hidden z-50">
                  <div className="px-3.5 py-3 border-b border-[#E5E9EE]">
                    <p className="text-sm font-semibold text-[#132238] truncate">{user?.name}</p>
                    <p className="text-[11px] text-[#667085]">{ROLE_LABEL[user?.role]}</p>
                  </div>
                  <button data-testid="menu-logout-btn" onMouseDown={doLogout}
                    className="w-full flex items-center gap-2 px-3.5 py-2.5 text-sm text-[#667085] hover:bg-[#F7F9FA] hover:text-[#132238] transition-colors duration-150">
                    <LogOut size={14} /> Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 px-4 sm:px-8 py-6 pb-24 md:pb-10 max-w-[1440px] w-full mx-auto">{children}</main>

        <nav className="no-print md:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-[#E5E9EE] flex">
          {items.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`tab-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium relative ${isActive ? "text-[#087F5B]" : "text-[#98A2B3]"}`
              }
            >
              <Icon size={19} /> {label}
              {label === "Coaching" && unreadCoaching > 0 && (
                <span className="absolute top-1.5 right-[calc(50%-16px)] w-2 h-2 rounded-full bg-red-500" />
              )}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
